import { useState, useMemo } from "react";
import React from "react";
import STATS from "./data/stats";
import RACES from "./data/races";
import MAGIC_SCHOOLS from "./data/magic_schools";
import STAR_SIGNS from "./data/star_signs";
import SKILLS from "./data/skills";
import {WEAPON_TYPES, WEAPON_QUALITIES, WEAPON_ABILITIES, RANGED_SUBTYPES} from "./data/weapons";
import {ARMOR_PIECES, ARMOR_QUALITIES, SHIELD_TYPES} from "./data/armor";
import { getAllSkills, getStatColor, getTimeAgo } from "./utils/helpers";
import { globalStyle, btnStyle } from "./styles/theme";
import BudgetItem from "./components/ui/BudgetItem";
import SectionHeader from "./components/ui/SectionHeader";
import ReviewSection from "./components/ui/ReviewSection";
import ExpPoolEditable from "./components/ui/ExpPoolEditable";
import EquipmentItem from "./components/ui/EquipmentItem";
import GearItem from "./components/ui/GearItem";
import WeaponEditor  from "./components/ui/WeaponEditor";
import IdentityStep from "./components/creation/IdentityStep";
import PerkStep from "./components/creation/PerkStep";
import ReviewStep from "./components/creation/ReviewStep";
import SkillStep from "./components/creation/SkillStep";
import StarSignStep from "./components/creation/StarSignStep";
import StatStep from "./components/creation/StatStep";
import CharacterCard from "./components/Library/CharacterCard";
import CharacterLibraryModal from "./components/Library/CharacterLibraryModal";
import LevelingScreen from "./components/leveling/LevelingScreen";
import LevelingView from "./components/leveling/LevelingView";
import CharacterDetails from './components/character/CharacterDetails';

import {
  STAT_POINTS_TO_ALLOCATE,
  PERKS_FROM_RACE,
  FREE_PERKS,
  SKILL_LEVELS_TO_ALLOCATE,
  STAT_COST,
  SKILL_COST,
  MAGIC_COST,
  ALCHEMY_COST,
  ABILITY_COST
} from './config/gameConfig';

// ─── MAIN APP ───────────────────────────────────────────────────────────────
export default function App() {
  const [mode, setMode] = useState("create"); // "create" or "level"
  const [step, setStep] = useState(0); // 0=Identity, 1=Stats, 2=Perks, 3=Skills, 4=StarSign, 5=Review
  const [character, setCharacter] = useState({
    name: "",
    race: null,
    statPoints: { might: 0, speed: 0, intellect: 0, magic: 0 }, // points allocated beyond race base
    selectedPerks: [],   // indices into race.perks (max 2)
    freePerk: null,      // a skill id chosen freely
    skills: {},          // { skillId: levels }
    starSign: null,
  });

  // Leveling state
  const [levelingChar, setLevelingChar] = useState(null); // full character state for leveling
  const [expPools, setExpPools] = useState({
    universal: 0,
    might: 0,
    speed: 0,
    intellect: 0,
  });

  // Save/Load state
  const [showCharacterLibrary, setShowCharacterLibrary] = useState(false);
  const [savedCharacters, setSavedCharacters] = useState([]);

  // Load saved characters from localStorage on mount
  React.useEffect(() => {
    loadSavedCharactersList();
  }, []);

  // ── LOCALSTORAGE FUNCTIONS ──
  function loadSavedCharactersList() {
    try {
      const saved = localStorage.getItem('nalssadda_characters');
      if (saved) {
        setSavedCharacters(JSON.parse(saved));
      }
    } catch (error) {
      console.error('Error loading saved characters:', error);
    }
  }

  function saveCharacterToStorage(char, expPools) {
    try {
      const savedChar = {
        id: `char_${Date.now()}`,
        savedAt: new Date().toISOString(),
        character: char,
        expPools: expPools,
      };

      const existing = [...savedCharacters];
      // Check if character with same name exists
      const existingIndex = existing.findIndex(c => c.character.name === char.name);

      if (existingIndex >= 0) {
        if (!window.confirm(`A character named "${char.name}" already exists. Overwrite?`)) {
          return false;
        }
        existing[existingIndex] = savedChar;
      } else {
        existing.push(savedChar);
      }

      localStorage.setItem('nalssadda_characters', JSON.stringify(existing));
      setSavedCharacters(existing);
      return true;
    } catch (error) {
      console.error('Error saving character:', error);
      alert('Failed to save character. Storage may be full.');
      return false;
    }
  }

  function loadCharacterFromStorage(savedChar) {
    setLevelingChar(savedChar.character);
    setExpPools(savedChar.expPools);
    setMode('level');
    setShowCharacterLibrary(false);
  }

  function deleteCharacterFromStorage(id) {
    if (!window.confirm('Delete this character? This cannot be undone.')) {
      return;
    }
    const updated = savedCharacters.filter(c => c.id !== id);
    localStorage.setItem('nalssadda_characters', JSON.stringify(updated));
    setSavedCharacters(updated);
  }

  function exportCharacterAsJSON(savedChar) {
    const dataStr = JSON.stringify(savedChar, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${savedChar.character.name.replace(/[^a-z0-9]/gi, '_')}_character.json`;
    link.click();
    URL.revokeObjectURL(url);
  }

  function importCharacterFromJSON(event) {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const imported = JSON.parse(e.target.result);
        // Validate structure
        if (!imported.character || !imported.character.name) {
          alert('Invalid character file format.');
          return;
        }

        // Add new ID and timestamp
        imported.id = `char_${Date.now()}`;
        imported.savedAt = new Date().toISOString();

        const existing = [...savedCharacters, imported];
        localStorage.setItem('nalssadda_characters', JSON.stringify(existing));
        setSavedCharacters(existing);
        alert(`Character "${imported.character.name}" imported successfully!`);
      } catch (error) {
        console.error('Error importing character:', error);
        alert('Failed to import character. Invalid file format.');
      }
    };
    reader.readAsText(file);
    event.target.value = ''; // Reset input
  }

  // Derived: final stats = race base + allocated points
  const finalStats = useMemo(() => {
    if (!character.race) return { might: 0, speed: 0, intellect: 0, magic: 0 };
    const race = RACES.find(r => r.id === character.race);
    return {
      might: race.stats.might + (character.statPoints.might || 0),
      speed: race.stats.speed + (character.statPoints.speed || 0),
      intellect: race.stats.intellect + (character.statPoints.intellect || 0),
      magic: race.stats.magic + (character.statPoints.magic || 0),
    };
  }, [character.race, character.statPoints]);

  const statPointsUsed = useMemo(() => {
    return Object.values(character.statPoints).reduce((a, b) => a + b, 0);
  }, [character.statPoints]);

  const skillLevelsUsed = useMemo(() => {
    return Object.values(character.skills).reduce((a, b) => a + b, 0);
  }, [character.skills]);

  const perksSelected = character.selectedPerks.length;

  // ── STEP NAVIGATION ──
  const canProceed = () => {
    switch (step) {
      case 0: return character.name.trim().length > 0 && character.race !== null;
      case 1: return statPointsUsed === STAT_POINTS_TO_ALLOCATE;
      case 2: return character.selectedPerks.length === PERKS_FROM_RACE && character.freePerk !== null;
      case 3: return skillLevelsUsed === SKILL_LEVELS_TO_ALLOCATE;
      case 4: return character.starSign !== null;
      default: return true;
    }
  };

  // ── SHARED STYLES ──
  const globalStyle = `
    @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;600;700&family=Cinzel+Decorative:wght@400;700&display=swap');
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body, #root { background: #0f0c0a; color: #e8dcc8; font-family: 'Palatino Linotype', 'Book Antiqua', Palatino, serif; min-height: 100vh; }
    input, select, textarea { background: transparent; border: none; outline: none; color: #e8dcc8; font-family: inherit; }
    input::-webkit-scrollbar, textarea::-webkit-scrollbar { display: none; }
  `;

  // ── CONVERT CREATED CHARACTER TO LEVELING FORMAT ──
  function startLeveling() {
    if (!character.name || !character.race) {
      alert("Please complete character creation first!");
      return;
    }

    const race = RACES.find(r => r.id === character.race);

    // Convert creation format to leveling format
    const levelingCharacter = {
      name: character.name,
      race: character.race,
      stats: {
        might: race.stats.might + (character.statPoints.might || 0),
        speed: race.stats.speed + (character.statPoints.speed || 0),
        intellect: race.stats.intellect + (character.statPoints.intellect || 0),
        magic: race.stats.magic + (character.statPoints.magic || 0),
      },
      skills: { ...character.skills },
      magics: {}, // { magicId: levels }
      perks: [
        ...character.selectedPerks.map(i => race.perks[i]),
        ...(character.freePerk ? [getAllSkills().find(s => s.id === character.freePerk)?.name] : [])
      ].filter(Boolean),
      starSign: character.starSign,
      alchemy: { auxiliary: 1, organic: 1, conversion: 1, augmentation: 1 },
      weapons: [],
      armor: {
        head: "none",
        arms: "none",
        body: "none",
        legs: "none",
      },
      shield: "none",
      equipment: [],
      gear: [],
    };

    setLevelingChar(levelingCharacter);
    setExpPools({ universal: 0, might: 0, speed: 0, intellect: 0 });
    setMode("level");
  }

  return (
    <>
      <style>{globalStyle}</style>
      <div style={{ minHeight: "100vh", background: "radial-gradient(ellipse at center, #1a1510 0%, #0f0c0a 70%)", position: "relative", overflow: "hidden" }}>
        {/* Texture overlay */}
        <div style={{ position: "fixed", inset: 0, backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E\")", opacity: 0.6, pointerEvents: "none", zIndex: 0 }} />

        {/* Header */}
        <div style={{ position: "relative", zIndex: 1, textAlign: "center", padding: "28px 16px 12px" }}>
          <div style={{ fontFamily: "'Cinzel Decorative', cursive", fontSize: "clamp(20px, 4vw, 30px)", color: "#c9a84c", letterSpacing: "3px", textShadow: "0 0 20px rgba(201,168,76,0.3)" }}>
            Tales of Nalssadda
          </div>
          <div style={{ fontFamily: "'Cinzel', serif", fontSize: "clamp(11px, 2vw, 14px)", color: "#8a7e5e", letterSpacing: "4px", textTransform: "uppercase", marginTop: "4px" }}>
            {mode === "create" ? "Character Creation" : "Character Leveling"}
          </div>

          {/* Mode Toggle */}
          {mode === "create" && step === 0 ? (
            // Initial screen - only Create New or Load Saved
            <div style={{ display: "flex", gap: "16px", justifyContent: "center", marginTop: "32px", flexWrap: "wrap" }}>
              <button onClick={() => {/* Already in create mode */}}
                style={{ ...btnStyle("#c9a84c", "rgba(201,168,76,0.2)"), padding: "12px 32px", fontSize: "14px" }}>
                ✨ Create New Character
              </button>
              <button onClick={() => setShowCharacterLibrary(true)}
                style={{ ...btnStyle("#8e44ad", "rgba(142,68,173,0.2)"), padding: "12px 32px", fontSize: "14px" }}>
                📚 Load Saved ({savedCharacters.length})
              </button>
            </div>
          ) : mode === "create" ? (
            // In creation flow - show minimal nav
            <div style={{ display: "flex", gap: "8px", justifyContent: "center", marginTop: "16px", flexWrap: "wrap" }}>
              <button onClick={() => { if(window.confirm('Return to start? Unsaved progress will be lost.')) { setStep(0); setCharacter({name: "", race: null, statPoints: {might:0,speed:0,intellect:0,magic:0}, selectedPerks:[], freePerk:null, skills:{}, starSign:null}); }}}
                style={{ ...btnStyle("#6b6048", "transparent"), padding: "6px 16px", fontSize: "11px" }}>
                ← Start Over
              </button>
              <button onClick={() => setShowCharacterLibrary(true)}
                style={{ ...btnStyle("#8e44ad", "rgba(142,68,173,0.15)"), padding: "6px 16px", fontSize: "11px" }}>
                Load Saved ({savedCharacters.length})
              </button>
            </div>
          ) : (
            // Leveling mode header
            <div style={{ display: "flex", gap: "8px", justifyContent: "center", marginTop: "16px", flexWrap: "wrap" }}>
              <button onClick={() => setMode("create")}
                style={{ ...btnStyle("#6b6048", "transparent"), padding: "6px 16px" }}>
                Create New
              </button>
              <button onClick={() => setShowCharacterLibrary(true)}
                style={{ ...btnStyle("#8e44ad", "rgba(142,68,173,0.15)"), padding: "6px 16px" }}>
                Load Saved ({savedCharacters.length})
              </button>
            </div>
          )}
        </div>

        {mode === "create" ? (
          <>
        {/* Step Navigation Bar - Only show if past step 0 */}
        {step > 0 && (
        <div style={{ position: "relative", zIndex: 1, display: "flex", justifyContent: "center", gap: "6px", padding: "0 12px", flexWrap: "wrap", marginBottom: "8px" }}>
          {["Identity", "Stats", "Perks", "Skills", "Star Sign", "Review"].map((label, i) => {
            const isActive = step === i;
            const isDone = step > i;
            return (
              <button key={i} onClick={() => { if (i < step || (i === step + 1 && canProceed())) setStep(i); }}
                style={{
                  background: isActive ? "linear-gradient(180deg, #3d2e1a, #2a1f0e)" : isDone ? "rgba(201,168,76,0.15)" : "rgba(30,25,18,0.6)",
                  border: isActive ? "1px solid #c9a84c" : isDone ? "1px solid rgba(201,168,76,0.4)" : "1px solid rgba(201,168,76,0.15)",
                  color: isActive ? "#c9a84c" : isDone ? "#a08d4e" : "#6b6048",
                  padding: "6px 12px", borderRadius: "4px", cursor: i <= step + 1 ? "pointer" : "default",
                  fontFamily: "'Cinzel', serif", fontSize: "11px", letterSpacing: "1px", transition: "all 0.2s",
                  opacity: i > step + 1 ? 0.4 : 1
                }}>
                {isDone ? "✓ " : ""}{label}
              </button>
            );
          })}
        </div>
        )}

        {/* Budget Bar */}
        {character.race && step >= 1 && step <= 4 && (
          <div style={{ position: "relative", zIndex: 1, maxWidth: 700, margin: "0 auto 16px", padding: "0 16px" }}>
            <div style={{ background: "rgba(20,16,10,0.8)", border: "1px solid rgba(201,168,76,0.2)", borderRadius: "6px", padding: "10px 16px", display: "flex", gap: "24px", flexWrap: "wrap", justifyContent: "center" }}>
              {step === 1 && (
                <BudgetItem label="Stat Points" used={statPointsUsed} total={STAT_POINTS_TO_ALLOCATE} color="#c9a84c" />
              )}
              {step === 2 && (
                <>
                  <BudgetItem label="Race Perks" used={perksSelected} total={PERKS_FROM_RACE} color="#c9a84c" />
                  <BudgetItem label="Free Perk" used={character.freePerk ? 1 : 0} total={FREE_PERKS} color="#8e44ad" />
                </>
              )}
              {step === 3 && (
                <BudgetItem label="Skill Levels" used={skillLevelsUsed} total={SKILL_LEVELS_TO_ALLOCATE} color="#c9a84c" />
              )}
            </div>
          </div>
        )}

        {/* Main Content */}
        <div style={{ position: "relative", zIndex: 1, maxWidth: 720, margin: "0 auto", padding: "0 16px 40px" }}>
          {step === 0 && <IdentityStep character={character} setCharacter={setCharacter} />}
          {step === 1 && <StatStep character={character} setCharacter={setCharacter} finalStats={finalStats} statPointsUsed={statPointsUsed} />}
          {step === 2 && <PerkStep character={character} setCharacter={setCharacter} />}
          {step === 3 && <SkillStep character={character} setCharacter={setCharacter} skillLevelsUsed={skillLevelsUsed} finalStats={finalStats} />}
          {step === 4 && <StarSignStep character={character} setCharacter={setCharacter} />}
          {step === 5 && <ReviewStep character={character} finalStats={finalStats} onSave={saveCharacterToStorage} />}
        </div>

        {/* Nav Buttons */}
        <div style={{ position: "relative", zIndex: 1, maxWidth: 720, margin: "0 auto", padding: "0 16px 32px", display: "flex", justifyContent: "space-between" }}>
          {step > 0 && (
            <button onClick={() => setStep(step - 1)} style={btnStyle("#6b6048", "transparent")}>← Back</button>
          )}
          <div style={{ flex: 1 }} />
          {step < 5 && (
            <button onClick={() => { if (canProceed()) setStep(step + 1); }}
              style={{ ...btnStyle(canProceed() ? "#c9a84c" : "#4a4030", canProceed() ? "rgba(201,168,76,0.15)" : "transparent"), cursor: canProceed() ? "pointer" : "default", opacity: canProceed() ? 1 : 0.5 }}>
              {step === 4 ? "Finish →" : "Next →"}
            </button>
          )}
        </div>
        </>
        ) : (
          /* LEVELING MODE */
          <LevelingScreen
            character={levelingChar}
            setCharacter={setLevelingChar}
            expPools={expPools}
            setExpPools={setExpPools}
            onSave={saveCharacterToStorage}
          />
        )}
      </div>

      {/* Character Library Modal */}
      {showCharacterLibrary && (
        <CharacterLibraryModal
          savedCharacters={savedCharacters}
          onLoad={loadCharacterFromStorage}
          onDelete={deleteCharacterFromStorage}
          onExport={exportCharacterAsJSON}
          onImport={importCharacterFromJSON}
          onClose={() => setShowCharacterLibrary(false)}
        />
      )}
    </>
  );
}
